import { AlertTriangle, Clock } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

import type { WorkOrderRow } from "./types"

/**
 * Corey's spreadsheet colored a row by where the car physically was. That
 * convention is the one thing every person on the team already reads fluently,
 * so it survives the port verbatim — only the mechanism changes, from a manual
 * cell fill to a tone the server derives from the timestamps:
 *
 *   no color  the car is still at the bad order location, nothing is moving
 *   yellow    billed to a shop / en route — it's moving but it isn't there
 *   orange    in the shop, the clock everyone watches
 *   green     back in service, done
 *
 * Deliberately not shadcn Badge variants: `destructive` would make "in shop"
 * read as an error, and it isn't one — it's the normal middle of the process.
 */
const TONE_CLASSES: Record<WorkOrderRow["tone"], string> = {
  at_location:
    "border-border bg-transparent text-muted-foreground dark:text-muted-foreground",
  transit:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/50 dark:text-amber-200",
  in_shop:
    "border-orange-400 bg-orange-100 text-orange-900 dark:border-orange-700/60 dark:bg-orange-950/50 dark:text-orange-200",
  done: "border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/50 dark:text-emerald-200",
}

const TONE_TITLES: Record<WorkOrderRow["tone"], string> = {
  at_location: "At the bad order location",
  transit: "Billed to a shop — in transit",
  in_shop: "In the shop",
  done: "Back in service",
}

export function StatusChip({
  status_label,
  tone,
  className,
}: Pick<WorkOrderRow, "status_label" | "tone"> & { className?: string }) {
  return (
    <Badge
      variant="outline"
      title={TONE_TITLES[tone]}
      className={cn(TONE_CLASSES[tone], className)}
    >
      {status_label}
    </Badge>
  )
}

/**
 * Who the clock is running against, and for how long. The days figure is BOLD
 * because it is the column the whole queue sorts on — a 38-day wait on a
 * disposition is the thing an operator is scanning for, and it should be
 * findable without reading a single word.
 */
export function WaitingOnCell({
  waiting_on_label,
  days_waiting,
  className,
}: Pick<WorkOrderRow, "waiting_on_label" | "days_waiting"> & {
  className?: string
}) {
  if (!waiting_on_label) {
    return <span className="text-muted-foreground text-sm">—</span>
  }

  // Past a month on one party the row stops being a status and starts being a
  // problem, so it gets the eye before the number is even read.
  const severe = (days_waiting ?? 0) >= 30
  const notable = (days_waiting ?? 0) >= 14

  return (
    <span className={cn("flex items-center gap-2 text-sm", className)}>
      <span className="text-muted-foreground">{waiting_on_label}</span>
      {days_waiting != null && (
        <span
          className={cn(
            "font-semibold tabular-nums",
            severe && "text-destructive",
            !severe && notable && "text-orange-600 dark:text-orange-400",
          )}
        >
          {days_waiting}d
        </span>
      )}
    </span>
  )
}

/** Promised-Out Date, red once the shop has blown through it. */
export function PodCell({
  pod_promised_on,
  pod_missed,
}: Pick<WorkOrderRow, "pod_promised_on" | "pod_missed">) {
  if (!pod_promised_on) {
    return <span className="text-muted-foreground text-sm">—</span>
  }

  return (
    <span
      className={cn(
        "flex items-center gap-1 text-sm tabular-nums",
        pod_missed && "text-destructive font-semibold",
      )}
    >
      {pod_missed && <AlertTriangle className="size-3.5" />}
      {formatDate(pod_promised_on)}
    </span>
  )
}

export function LastFollowUpCell({ value }: { value: string | null }) {
  if (!value) {
    return (
      <span className="text-muted-foreground flex items-center gap-1 text-sm">
        <Clock className="size-3.5" />
        Never
      </span>
    )
  }
  return <span className="text-sm tabular-nums">{formatDate(value)}</span>
}

/**
 * Dates arrive as ISO strings from Rails. Rendered short (Jul 29) because every
 * table cell in this product is a date and the year is noise until it isn't —
 * so it appears only when the date is outside the current year.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  const sameYear = date.getUTCFullYear() === new Date().getUTCFullYear()
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    timeZone: "UTC",
  })
}
